import { FC, memo, useCallback, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { remify } from '#/service/utils';
import { tb, useTranslationStore } from '#/store/translation/translation.store';

interface Props {
  className?: string;
  onClick: () => void;
  isSecondary?: boolean;
}

const StyledImage = styled.img`
  ${(props) => !props.src && 'display: none;'}
  max-width: ${remify(100)};
  max-height: ${remify(80)};
  cursor: pointer;
`;

const NoLogo = styled.div`
  width: ${remify(50)};

  /* zero out the padding that MenuNav applies to all children */
  && {
    padding: 0;
  }
`;

const LogoBase: FC<Props> = ({ className, isSecondary, onClick }) => {
  const theme = useTheme();
  // We use the translations hook to force a re-render when the translations are initialized,
  // to make sure alt text is populated
  useTranslationStore();

  const [isError, setIsError] = useState(false);

  const src =
    isSecondary && theme.logo?.secondary
      ? theme.logo.secondary
      : theme.logo?.primary;

  useEffect(() => {
    setIsError(false);
  }, [src]);

  const onError = useCallback(() => setIsError(true), []);

  if (isError || !src) {
    return <NoLogo data-testid="no-logo" />;
  }

  return (
    <StyledImage
      alt={tb('tts.app.logo')}
      className={className}
      data-testid="app-logo"
      onClick={onClick}
      onError={onError}
      src={src}
    />
  );
};

export const Logo = memo(LogoBase);

// for pages without Menu, that still need a logo in the corner
export const LogoContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  padding: 0 0 0 ${remify(50)};
  display: flex;
  z-index: 1;
  -webkit-box-align: center;
  align-items: center;
  color: var(--pageParagraphForeground);
  line-height: 4.4444rem;
  user-select: none;
  padding-bottom: 0;
  pointer-events: auto;
  transition:
    background 300ms ease-in-out,
    transform 300ms ease-in-out;
  background: var(--menuOpaqueBackground);
  height: ${remify(80)};

  /* centered & scrolling with the content on small screens */
  @media ${MEDIA_QUERIES.lg} {
    position: absolute;
    box-sizing: border-box;
    margin: 0;
    padding: 0 0 0 ${remify(25)};
    width: 100%;
    height: ${remify(80)};
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
`;

LogoContainer.displayName = 'LogoContainer';
